import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthStore } from '../stores/authStore';
import { 
  shareApi, 
  ShareDataResponse, 
  ShareInfo, 
  buildShareUrl 
} from '../services/shareApi';

import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { WorkflowReadOnlyCanvas } from '../components/workflow/WorkflowReadOnlyCanvas';
import { DiffResultView } from '../components/workflow/DiffResultView';
import { 
  Lock, 
  Copy, 
  Check, 
  Calendar,
  Lightning,
  CaretDown,
  ArrowsLeftRight,
  Table,
} from '@phosphor-icons/react';
import {
  AlertCircle,
  Loader2,
  FileSpreadsheet,
  Unlock,
  Workflow,
} from 'lucide-react';
import type { WorkflowExecutionResult } from '../types/template';

// 密码验证组件
function PasswordVerify({ 
  onVerify, 
  error, 
  isVerifying 
}: { 
  onVerify: (password: string) => void;
  error: string | null;
  isVerifying: boolean;
}) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    onVerify(password);
  }, [onVerify, password]);

  return (
    <Card className="max-w-md mx-auto mt-20">
      <CardHeader className="text-center">
        <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
          <Lock className="w-6 h-6 text-blue-600" />
        </div>
        <CardTitle>{t('share.password.title')}</CardTitle>
        <CardDescription>
          {t('share.password.description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('share.password.placeholder')}
            maxLength={6}
            className="font-mono text-center tracking-widest"
            autoFocus
          />
          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{error}</span>
            </div>
          )}
          <Button 
            type="submit" 
            className="w-full"
            disabled={isVerifying || password.length < 4}
          >
            {isVerifying ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('share.password.verifying')}
              </>
            ) : (
              <>
                <Unlock className="w-4 h-4 mr-2" />
                {t('share.password.verify')}
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// 错误展示组件
function ErrorDisplay({ 
  title, 
  message, 
  action 
}: { 
  title: string; 
  message: string;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <Card className="max-w-md mx-auto mt-20">
      <CardHeader className="text-center">
        <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <AlertCircle className="w-6 h-6 text-red-600" />
        </div>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{message}</CardDescription>
      </CardHeader>
      {action && (
        <CardContent>
          <Button onClick={action.onClick} className="w-full">
            {action.label}
          </Button>
        </CardContent>
      )}
    </Card>
  );
}

// 可折叠区块组件
function CollapsibleSection({
  title,
  icon: Icon,
  iconColor,
  children,
  defaultExpanded = true,
  rightContent,
}: {
  title: string;
  icon: React.ElementType;
  iconColor: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  rightContent?: React.ReactNode;
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className="flex flex-col min-h-0 flex-1">
      {/* Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between px-4 py-2 bg-zinc-50/80 border-b border-zinc-200 hover:bg-zinc-100/80 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className={`w-3.5 h-3.5 ${iconColor}`} weight="fill" />
          <span className="text-xs font-medium text-zinc-700">{title}</span>
        </div>
        <div className="flex items-center gap-2">
          {rightContent}
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <CaretDown className="w-3.5 h-3.5 text-zinc-400" weight="bold" />
          </motion.div>
        </div>
      </button>

      {/* Collapsible Content */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="overflow-hidden flex-1"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// 普通数据表格组件
function DataTable({
  data,
  columns,
  maxRows = 10,
}: {
  data: Record<string, unknown>[];
  columns: string[];
  maxRows?: number;
}) {
  const displayData = data.slice(0, maxRows);

  const PNG_DATA_URL_PREFIX = 'data:image/png;base64,';

  function getCellImageSrc(value: unknown): string | null {
    if (value == null) return null;
    const s = typeof value === 'string' ? value.trim() : String(value);
    if (!s) return null;
    if (s.startsWith(PNG_DATA_URL_PREFIX)) return s;
    if (s.length > 50 && !s.includes(' ') && /^[A-Za-z0-9+/=]+$/.test(s)) return PNG_DATA_URL_PREFIX + s;
    return null;
  }

  function formatCellValue(value: unknown): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') {
      if (value.length > 100 && /[\x00-\x08\x0B\x0C\x0E-\x1F]/.test(value)) return '[Binary]';
      return value;
    }
    if (typeof value === 'number') return String(value);
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (value instanceof Date) return value.toISOString().split('T')[0];
    if (typeof value === 'object') {
      const obj = value as Record<string, unknown>;
      if ('w' in obj && typeof obj.w === 'string') return obj.w;
      if ('v' in obj) {
        const val = obj.v;
        if (val === null || val === undefined) return '';
        if (typeof val === 'string') return val;
        if (typeof val === 'number') return String(val);
        if (val instanceof Date) return val.toISOString().split('T')[0];
        return String(val);
      }
      try {
        const jsonStr = JSON.stringify(value);
        return jsonStr.length > 50 ? jsonStr.substring(0, 50) + '...' : jsonStr;
      } catch {
        return '[Object]';
      }
    }
    return String(value);
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-zinc-50 sticky top-0">
            <tr>
              {columns.map((col) => (
                <th
                  key={col}
                  className="px-3 py-2 text-left font-medium text-zinc-600 border-b border-zinc-200 whitespace-nowrap"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayData.map((row, idx) => (
              <tr key={idx} className="hover:bg-zinc-50/50">
                {columns.map((col) => {
                  const cellVal = row[col];
                  const imgSrc = getCellImageSrc(cellVal);
                  return (
                    <td key={col} className="px-3 py-2 text-zinc-700 border-b border-zinc-100 align-middle">
                      {imgSrc ? (
                        <img src={imgSrc} alt="" className="max-h-[72px] max-w-[120px] w-auto h-auto object-contain block" />
                      ) : (
                        <span className="whitespace-nowrap">{formatCellValue(cellVal)}</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

        {displayData.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-10 h-10 rounded-lg bg-zinc-100 flex items-center justify-center mb-2">
              <Table className="w-5 h-5 text-zinc-300" />
            </div>
            <p className="text-xs text-zinc-400">暂无数据</p>
          </div>
        )}
      </div>
    </div>
  );
}

// 分组结果组件
function GroupedResults({
  groups,
  totalRecords,
}: {
  groups: Array<{
    group_key: string;
    columns: string[];
    rows: Record<string, unknown>[];
    rowCount: number;
  }>;
  totalRecords: number;
}) {
  const [activeTab, setActiveTab] = useState(0);
  const { t } = useTranslation();

  return (
    <div className="flex flex-col h-full">
      {/* Tabs */}
      <div className="flex items-center gap-1 px-2 py-2 border-b border-zinc-200 bg-zinc-50/50 overflow-x-auto">
        {groups.map((g, idx) => (
          <button
            key={idx}
            onClick={() => setActiveTab(idx)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors ${
              activeTab === idx
                ? 'bg-emerald-100 text-emerald-700'
                : 'text-zinc-600 hover:bg-zinc-100'
            }`}
          >
            {g.group_key}
            <span className="ml-1 text-zinc-400">({g.rowCount})</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {groups[activeTab] && (
          <DataTable
            data={groups[activeTab].rows}
            columns={groups[activeTab].columns}
            maxRows={10}
          />
        )}
      </div>

      {/* Summary */}
      <div className="px-4 py-2 bg-zinc-50 border-t border-zinc-200 text-xs text-zinc-500">
        {t('workflow.totalGroups', { count: groups.length })} · {t('workflow.totalRecords', { count: totalRecords })}
      </div>
    </div>
  );
}

// 检查是否是 diff 结果
function isDiffResult(columns: string[]): boolean {
  return columns.includes('diff_type');
}

// 主页面组件
export function SharePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuthStore();
  
  // 状态
  const [loading, setLoading] = useState(true);
  const [shareInfo, setShareInfo] = useState<ShareInfo | null>(null);
  const [shareData, setShareData] = useState<ShareDataResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [passwordVerified, setPasswordVerified] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  
  // 复制状态
  const [copiedLink, setCopiedLink] = useState(false);
  const [isCopyingWorkflow, setIsCopyingWorkflow] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // 获取分享信息
  useEffect(() => {
    if (!token) return;

    const fetchShareInfo = async () => {
      try {
        setLoading(true);
        const response = await shareApi.getShareInfo(token);
        console.log('fetch token response', response);
        
        if (response.code === 0) {
          setShareInfo(response.data);
          
          // 如果不需要密码，直接获取数据
          if (!response.data.requirePassword) {
            await fetchShareData();
          }
        } else {
          setError(response.message);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : '获取分享信息失败');
      } finally {
        setLoading(false);
      }
    };

    fetchShareInfo();
  }, [token]);

  // 获取分享数据
  const fetchShareData = async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const response = await shareApi.getShareData(token);

      console.log('response data', response);
      
      if (response.code === 0) {
        setShareData(response.data);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取分享数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 验证密码
  const handleVerifyPassword = useCallback(async (password: string) => {
    if (!token) return;
    
    setIsVerifying(true);
    setVerifyError(null);

    try {
      const response = await shareApi.verifyPassword(token, password);
      
      if (response.code === 0 && response.data.verified) {
        setPasswordVerified(true);
        await fetchShareData();
      } else {
        setVerifyError('密码错误，请重试');
      }
    } catch (err) {
      setVerifyError(err instanceof Error ? err.message : '验证失败');
    } finally {
      setIsVerifying(false);
    }
  }, [token]);

  // 复制链接
  const handleCopyLink = useCallback(async () => {
    if (!token) return;
    
    try {
      await navigator.clipboard.writeText(buildShareUrl(token));
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch {
      // 复制失败
    }
  }, [token]);

  // 复制工作流
  const handleCopyWorkflow = useCallback(async () => {
    if (!token) return;

    // 如果未登录，保存分享信息到 localStorage，然后跳转到登录页面
    if (!isAuthenticated) {
      localStorage.setItem('pending_share_copy', JSON.stringify({
        shareToken: token,
        workflowName: shareData?.shareInfo?.workflowName || '工作流',
      }));
      navigate('/login', { state: { from: window.location.pathname } });
      return;
    }

    setIsCopyingWorkflow(true);
    setCopySuccess(false);

    try {
      const response = await shareApi.copyWorkflow(token, {
        newName: `${shareData?.shareInfo?.workflowName || '工作流'} (复制)`,
      });

      if (response.code === 0 && response.data?.id) {
        setCopySuccess(true);
        // 3秒后跳转到工作流编辑页
        setTimeout(() => {
          navigate('/workflows', {replace: true});
          // navigate(`/workflows/store/edit/${response.data.id}`);
        }, 500);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '复制失败');
    } finally {
      setIsCopyingWorkflow(false);
    }
  }, [token, isAuthenticated, navigate, shareData?.shareInfo?.workflowName]);

  // 准备执行结果数据
  const executionResult: WorkflowExecutionResult | null = useMemo(() => {
    if (!shareData) return null;
    return {
      columns: shareData.columns || [],
      rows: shareData.data || [],
      rowCount: shareData.totalRows || 0,
      metadata: shareData.metadata,
      is_grouped: shareData.is_grouped,
      grouped_results: shareData.grouped_results,
    };
  }, [shareData]);

  // 是否是 diff 结果
  const isDiff = useMemo(() => {
    if (!shareData?.columns) return false;
    return isDiffResult(shareData.columns);
  }, [shareData]);

  // 是否是分组结果
  const isGrouped = useMemo(() => {
    return shareData?.is_grouped && shareData?.grouped_results && shareData.grouped_results.length > 0;
  }, [shareData]);

  // 获取来源标签
  const sourceLabel = useMemo(() => {
    const sourceType = shareInfo?.sourceType || shareData?.shareInfo?.sourceType;
    const versionNum = shareInfo?.versionNum || shareData?.shareInfo?.versionNum;
    
    if (sourceType === 'template') {
      return t('share.source.template');
    }
    
    if (versionNum && versionNum > 0) {
      return `${t('share.source.workflow')} · ${t('share.source.version', { version: versionNum })}`;
    }
    
    return `${t('share.source.workflow')} · ${t('share.source.noVersion')}`;
  }, [shareInfo, shareData, t]);

  // 渲染加载状态
  if (loading && !shareData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // 渲染错误状态
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <ErrorDisplay
          title={t('share.errors.accessDenied')}
          message={error}
          action={{
            label: t('share.errors.backToHome'),
            onClick: () => navigate('/'),
          }}
        />
      </div>
    );
  }

  // 渲染密码验证
  if (shareInfo?.requirePassword && !passwordVerified && !shareData) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <PasswordVerify
          onVerify={handleVerifyPassword}
          error={verifyError}
          isVerifying={isVerifying}
        />
      </div>
    );
  }

  // 渲染结果内容
  const renderResultContent = () => {
    if (!executionResult) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <FileSpreadsheet className="w-12 h-12 text-zinc-300 mb-3" />
          <p className="text-zinc-500">{t('share.data.noData')}</p>
        </div>
      );
    }

    // 数据展示不需要登录，按照分享设置显示（受数据条数限制）
    if (isDiff) {
      return (
        <DiffResultView
          data={executionResult.rows}
          columns={executionResult.columns}
          metadata={executionResult.metadata}
        />
      );
    }

    if (isGrouped && executionResult.grouped_results) {
      const totalRecords = executionResult.grouped_results.reduce((sum, g) => sum + g.rowCount, 0);
      return (
        <GroupedResults
          groups={executionResult.grouped_results}
          totalRecords={totalRecords}
        />
      );
    }

    return (
      <DataTable
        data={executionResult.rows}
        columns={executionResult.columns}
        maxRows={10}
      />
    );
  };

  const displayShareInfo = shareData?.shareInfo || shareInfo;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 头部 */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Workflow className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-lg">
                {displayShareInfo?.workflowName || t('share.pageTitle')}
              </h1>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Calendar className="w-3 h-3" />
                <span>
                  {t('share.header.createdAt', { 
                    date: new Date(displayShareInfo?.createdAt || '').toLocaleDateString() 
                  })}
                </span>
                <span className="text-gray-300">·</span>
                <span className="text-blue-600">{sourceLabel}</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleCopyLink}
            >
              {copiedLink ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {t('share.header.copied')}
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  {t('share.header.copyLink')}
                </>
              )}
            </Button>
            <Button
              size="sm"
              onClick={handleCopyWorkflow}
              disabled={isCopyingWorkflow || copySuccess}
            >
              {isCopyingWorkflow ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('share.header.copying')}
                </>
              ) : copySuccess ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {t('share.header.copySuccess')}
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  {isAuthenticated ? t('share.header.copyWorkflow') : t('share.header.loginToCopy')}
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* 主内容 */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-[calc(100vh-140px)]">
          {/* 工作流展示 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 100, damping: 20 }}
            className="bg-white border border-zinc-200/50 rounded-2xl overflow-hidden shadow-[0_20px_40px_-15px_rgba(0,0,0,0.05)] flex flex-col"
          >
            <CollapsibleSection
              title={t('share.sections.workflowStructure')}
              icon={Workflow}
              iconColor="text-blue-600"
              defaultExpanded={true}
            >
              <div className="h-full p-4">
                {shareData?.workflow && shareData.workflow.nodes && shareData.workflow.nodes.length > 0 ? (
                  <div className="h-full border rounded-lg overflow-hidden bg-gray-50">
                    <WorkflowReadOnlyCanvas 
                      nodes={shareData.workflow.nodes.map(n => ({
                        id: n.id,
                        type: n.type,
                        position: n.position || { x: 0, y: 0 },
                        config: { 
                          type: n.type,
                          params: n.config || {} 
                        },
                        label: n.label,
                      }))}
                      edges={shareData.workflow.edges?.map(e => ({
                        from: e.source,
                        to: e.target,
                      })) || []}
                    />
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-zinc-400">
                    <Workflow className="w-12 h-12 mb-3 text-zinc-300" />
                    {shareData?.workflow === undefined ? (
                      <p>{t('share.sections.workflowNotAvailable')}</p>
                    ) : (
                      <p>{t('share.sections.workflowNoNodes')}</p>
                    )}
                  </div>
                )}
              </div>
            </CollapsibleSection>
          </motion.div>

          {/* 执行结果数据 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 100, damping: 20, delay: 0.1 }}
            className="bg-white border border-zinc-200/50 rounded-2xl overflow-hidden shadow-[0_20px_40px_-15px_rgba(0,0,0,0.05)] flex flex-col"
          >
            <CollapsibleSection
              title={isDiff ? t('share.sections.diffResult') : t('share.sections.executionResult')}
              icon={isDiff ? ArrowsLeftRight : Lightning}
              iconColor={isDiff ? 'text-violet-500' : 'text-amber-500'}
              defaultExpanded={true}
              rightContent={
                executionResult && (
                  <span className="text-[10px] text-emerald-600 font-medium">
                    {isGrouped
                      ? t('workflow.totalGroups', { count: executionResult.grouped_results?.length || 0 })
                      : t('nodes.rows', { count: executionResult.rowCount })}
                  </span>
                )
              }
            >
              <div className="h-full flex flex-col">
                {/* 数据展示不需要登录，按照分享设置显示 */}
                <div className="flex-1 overflow-hidden">
                  {renderResultContent()}
                </div>
              </div>
            </CollapsibleSection>
          </motion.div>
        </div>
      </main>
    </div>
  );
}

export default SharePage;
